import { useEffect, useState } from 'react';
import type { SessionUser } from '@helpdesk/shared';
import { postJson } from '../api/client.ts';

type Account = SessionUser['authSource'];

const MARKER = 'hd.logout-pending';
const LOGGED_OUT_PATH = '/abgemeldet';
/** Ein hängender POST im Hallen-WLAN dauert sonst Minuten, und solange steht das Konto offen auf dem Bildschirm. */
const POST_TIMEOUT_MS = 8000;
/** Im WLAN ohne Uplink feuert `online` nie. */
const RETRY_MS = 30_000;

export const LOGOUT_PENDING_NOTE = 'Die Abmeldung hat den Server noch nicht erreicht. Sie wird nachgeholt, sobald wieder Netz da ist.';

/**
 * Das httpOnly-Cookie kann nur der Server beenden. Bis er das bestätigt, steht hier die Kontoart.
 * Danach steht hier das Ziel bei Microsoft, falls noch offen. Steht etwas da, gilt niemand als angemeldet.
 * Fehlt `localStorage`, fehlt meist auch das Sitzungs-Cookie, und `beginLogout` sendet trotzdem.
 */
function marker(): string | null {
  try {
    return localStorage.getItem(MARKER);
  } catch {
    return null;
  }
}

function setMarker(value: string | null): void {
  try {
    if (value) localStorage.setItem(MARKER, value);
    else localStorage.removeItem(MARKER);
  } catch {
    /* siehe `marker` */
  }
}

export function logoutPending(): boolean {
  return marker() !== null;
}

let account: Account = 'entra';

/** Hält die Kontoart fest: Ohne Sitzung kennt sie der Server nicht mehr, und dann hält er jedes Konto für ein Microsoft-Konto. */
export function noteAccount(source: Account): void {
  account = source;
}

let running: Promise<boolean> | null = null;

/** Ein zweiter POST fände keine Sitzung mehr und bekäme ein Ziel ohne `logout_hint`. Darum läuft höchstens einer. */
function postLogout(source: Account): Promise<boolean> {
  running ??= Promise.race([
    postJson<{ logoutUrl: string }>('/api/auth/logout').then(({ logoutUrl }) => {
      setMarker(source === 'local' || logoutUrl === LOGGED_OUT_PATH ? null : logoutUrl);
      return true;
    }),
    new Promise<boolean>((resolve) => setTimeout(() => resolve(false), POST_TIMEOUT_MS)),
  ])
    .catch(() => false)
    .finally(() => {
      running = null;
    });
  return running;
}

/** Der Merker steht vor dem POST: Bricht die Seite mittendrin ab, ist die Abmeldung nicht vergessen. */
export function beginLogout(): Promise<boolean> {
  setMarker(account);
  return postLogout(account);
}

/** Holt den POST nach, falls er offen ist. `false`: Die Sitzung kann noch bestehen. */
export function endServerSession(): Promise<boolean> {
  const state = marker();
  return state === 'entra' || state === 'local' ? postLogout(state) : Promise.resolve(true);
}

/** Das offene Ziel bei Microsoft, höchstens einmal. Nur von Seiten aus aufrufen, auf denen keine Eingabe verloren geht. */
export function takeEntraLogout(): string | null {
  const state = marker();
  if (!state || state === 'entra' || state === 'local') return null;
  setMarker(null);
  return state;
}

/** Bringt eine ausstehende Abmeldung zu Ende: beim Öffnen der Seite, bei Netzrückkehr und im Takt. */
export function usePendingLogout(): boolean {
  const [pending, setPending] = useState(logoutPending);
  useEffect(() => {
    if (!pending) return;
    const retry = () => {
      void endServerSession().then((ended) => {
        if (!ended) return;
        const entra = takeEntraLogout();
        if (entra) window.location.href = entra;
        else setPending(false);
      });
    };
    retry();
    window.addEventListener('online', retry);
    const timer = setInterval(retry, RETRY_MS);
    return () => {
      window.removeEventListener('online', retry);
      clearInterval(timer);
    };
  }, [pending]);
  return pending;
}
